// CPV Hierarchy utilities for frontend
// Implements hierarchical browsing and search functionality for CPV codes

interface CPVCode {
  code: string
  description_pl: string
  description_en: string
  parent_code: string | null
  level: number
}

interface CPVTreeNode extends CPVCode {
  children: CPVTreeNode[]
  expanded: boolean
  visible: boolean
}

/**
 * Build CPV code hierarchy tree from flat data
 * @param {CPVCode[]} cpvCodes - Flat array of CPV codes
 * @returns {CPVTreeNode[]} Hierarchical tree structure
 */
export function buildCPVTree(cpvCodes: CPVCode[]): CPVTreeNode[] {
  if (!cpvCodes || cpvCodes.length === 0) return []

  // Group by parent
  const byParent: Map<string | null, CPVCode[]> = new Map()
  cpvCodes.forEach((code) => {
    const parent = code.parent_code || null
    if (!byParent.has(parent)) {
      byParent.set(parent, [])
    }
    byParent.get(parent)!.push(code)
  })

  // Recursively build tree
  function buildTree(parentCode: string | null): CPVTreeNode[] {
    const children = (byParent.get(parentCode) || []).map((code: CPVCode) => ({
      ...code,
      children: buildTree(code.code),
      expanded: false,
      visible: true,
    }))

    // Sort by code
    return children.sort((a: CPVTreeNode, b: CPVTreeNode) => a.code.localeCompare(b.code))
  }

  return buildTree(null)
}

/**
 * Filter CPV codes based on search criteria
 * @param {CPVTreeNode[]} cpvTree - Hierarchical tree of CPV codes
 * @param {string} searchTerm - Search term
 * @param {string[]} selectedCodes - Already selected codes
 * @returns {CPVTreeNode[]} Filtered CPV codes
 */
export function filterCPVCodes(
  cpvTree: CPVTreeNode[],
  searchTerm: string = '',
  selectedCodes: string[] = [],
): CPVTreeNode[] {
  if (!searchTerm.trim()) {
    return cpvTree
  }

  const term = searchTerm.toLowerCase().trim()

  function filterTree(nodes: CPVTreeNode[]): CPVTreeNode[] {
    return nodes
      .map((node) => {
        // Check if current node matches
        const matches = matchesSearch(node, term)

        // Filter children
        const filteredChildren = filterTree(node.children || [])

        // Include node if it matches or has matching children
        if (matches || filteredChildren.length > 0) {
          return {
            ...node,
            children: filteredChildren,
            expanded: true, // Expand matching nodes
            visible: true,
          }
        }

        return null
      })
      .filter((node) => node !== null) as CPVTreeNode[]
  }

  return filterTree(cpvTree)
}

/**
 * Get CPV code path (breadcrumbs)
 * @param {string} code - CPV code
 * @param {CPVCode[]} allCodes - All CPV codes (flat array)
 * @returns {Array} Array of parent codes including the target code
 */
export function getCPVPath(code: string, allCodes: CPVCode[]): CPVCode[] {
  if (!allCodes || !code) return []

  // Create lookup map
  const codeMap: Record<string, CPVCode> = {}
  allCodes.forEach((cpv) => {
    codeMap[cpv.code] = cpv
  })

  const path: CPVCode[] = []
  let currentCode = code

  while (currentCode) {
    const cpv = codeMap[currentCode]
    if (!cpv) break

    path.unshift({
      code: cpv.code,
      description_pl: cpv.description_pl,
      description_en: cpv.description_en,
      parent_code: cpv.parent_code,
      level: cpv.level,
    })

    currentCode = cpv.parent_code || ''
  }

  return path
}

/**
 * Check if a CPV code matches search criteria
 * @param {CPVCode} cpvCode - CPV code object
 * @param {string} searchTerm - Search term
 * @returns {boolean} Whether the code matches
 */
export function matchesSearch(cpvCode: CPVCode, searchTerm: string): boolean {
  if (!searchTerm) return true

  const term = searchTerm.toLowerCase()
  return (
    cpvCode.code?.toLowerCase().includes(term) ||
    cpvCode.description_pl?.toLowerCase().includes(term) ||
    cpvCode.description_en?.toLowerCase().includes(term)
  )
}

/**
 * Expand tree to specified level
 * @param {CPVTreeNode[]} tree - CPV tree
 * @param {number} level - Level to expand to
 * @returns {CPVTreeNode[]} Tree with expanded nodes
 */
export function expandToLevel(tree: CPVTreeNode[], level: number = 2): CPVTreeNode[] {
  function expandNode(node: CPVTreeNode, currentLevel: number = 1): CPVTreeNode {
    const shouldExpand = currentLevel < level
    return {
      ...node,
      expanded: shouldExpand,
      children: node.children?.map((child) => expandNode(child, currentLevel + 1)) || [],
    }
  }

  return tree.map((node) => expandNode(node))
}

/**
 * Find node in tree by code
 * @param {CPVTreeNode[]} tree - CPV tree
 * @param {string} code - Code to find
 * @returns {CPVTreeNode|null} Found node or null
 */
export function findNodeByCode(tree: CPVTreeNode[], code: string): CPVTreeNode | null {
  for (const node of tree) {
    if (node.code === code) {
      return node
    }
    if (node.children) {
      const found = findNodeByCode(node.children, code)
      if (found) return found
    }
  }
  return null
}

/**
 * Toggle node expansion
 * @param {CPVTreeNode[]} tree - CPV tree
 * @param {string} code - Code of node to toggle
 * @returns {CPVTreeNode[]} Updated tree
 */
export function toggleNodeExpansion(tree: CPVTreeNode[], code: string): CPVTreeNode[] {
  function toggleInTree(nodes: CPVTreeNode[]): CPVTreeNode[] {
    return nodes.map((node) => {
      if (node.code === code) {
        return { ...node, expanded: !node.expanded }
      }
      if (node.children) {
        return { ...node, children: toggleInTree(node.children) }
      }
      return node
    })
  }

  return toggleInTree(tree)
}

/**
 * Get all selected codes from tree (including children of selected parents)
 * @param {CPVTreeNode[]} tree - CPV tree
 * @param {string[]} selectedCodes - Selected parent codes
 * @returns {string[]} All selected codes
 */
export function getAllSelectedCodes(tree: CPVTreeNode[], selectedCodes: string[]): string[] {
  const allSelected = new Set<string>()

  function collectSelected(nodes: CPVTreeNode[], includeAll: boolean = false): void {
    nodes.forEach((node) => {
      const isSelected = selectedCodes.includes(node.code)
      const shouldInclude = includeAll || isSelected

      if (shouldInclude) {
        allSelected.add(node.code)
      }

      // If parent is selected, include all children
      if (node.children) {
        collectSelected(node.children, shouldInclude)
      }
    })
  }

  collectSelected(tree)
  return Array.from(allSelected)
}

/**
 * Check if code is selected (including through parent selection)
 * @param {string} code - Code to check
 * @param {string[]} selectedCodes - Selected codes
 * @param {CPVCode[]} allCodes - All CPV codes for hierarchy lookup
 * @returns {boolean} Whether code is selected
 */
export function isCodeSelected(
  code: string,
  selectedCodes: string[],
  allCodes: CPVCode[],
): boolean {
  // Direct selection
  if (selectedCodes.includes(code)) return true

  // Check if any parent is selected
  const path = getCPVPath(code, allCodes)
  return path.some((step) => selectedCodes.includes(step.code))
}
